import { ActionIcon, Box, Text, Tooltip } from '@mantine/core';
import { IconSend, IconX } from '@tabler/icons-react';
import { VoiceWaveform } from './VoiceWaveform';

interface AudioRecordingOverlayProps {
  durationMs: number;
  audioLevel: number;
  transcript: string;
  isTranscriptAvailable: boolean;
  onCancel: () => void;
  onSend: () => void;
  sending?: boolean;
}

function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export function AudioRecordingOverlay({
  durationMs,
  audioLevel,
  transcript,
  isTranscriptAvailable,
  onCancel,
  onSend,
  sending,
}: AudioRecordingOverlayProps) {
  return (
    <Box
      style={{
        background: '#1A1A1E',
        border: '1px solid #EF4444',
        borderRadius: 16,
        padding: '10px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {/* Top row: timer + waveform + buttons */}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Recording dot + timer */}
        <Box style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 56 }}>
          <Box
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#EF4444',
              animation: 'pulse 1.2s ease-in-out infinite',
            }}
          />
          <Text size="xs" fw={600} c="#EF4444" ff="monospace">
            {formatDuration(durationMs)}
          </Text>
        </Box>

        {/* Waveform */}
        <Box style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <VoiceWaveform audioLevel={audioLevel} />
        </Box>

        {/* Cancel button */}
        <Tooltip label="Cancel" position="top" withArrow>
          <ActionIcon variant="subtle" color="gray" size="sm" onClick={onCancel}>
            <IconX size={16} />
          </ActionIcon>
        </Tooltip>

        {/* Send button */}
        <Tooltip label="Send recording" position="top" withArrow>
          <ActionIcon
            variant="filled"
            color="blue"
            size="sm"
            radius="xl"
            onClick={onSend}
            loading={sending}
          >
            <IconSend size={14} />
          </ActionIcon>
        </Tooltip>
      </Box>

      {/* Transcript row */}
      {isTranscriptAvailable && transcript && (
        <Text size="xs" c="#A1A1AA" lineClamp={2} style={{ fontStyle: 'italic' }}>
          "{transcript}"
        </Text>
      )}

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </Box>
  );
}
